import { useEffect, useRef, useState } from "react";
import { ApiError, type ApiClient } from "../../../api/api-client";
import { rewardPoolSchema } from "../../../api/schemas";
import { ELYSIUM, sameIdentity } from "../../../config/elysium";
import { errorMessage } from "../../../state/model-helpers";

export type PoolSnapshot = ReturnType<typeof rewardPoolSchema.parse>;
export function validateElysiumPool(value: PoolSnapshot): PoolSnapshot {
  if (value.chainId !== ELYSIUM.chainId || !sameIdentity(value.sourceToken, ELYSIUM.token) ||
    !sameIdentity(value.vaultAddress, ELYSIUM.vault) || !sameIdentity(value.collectionAddress, ELYSIUM.collection) ||
    value.balanceMode !== "SETTLED_VAULT") throw new Error("REWARD_POOL_CONTEXT_MISMATCH");
  if (!value.poolAvailable || value.dataStatus !== "CURRENT" || value.availableRewardWei === null ||
    value.settledRewardWei !== value.availableRewardWei || value.asOfBlock === null || value.asOfBlockHash === null ||
    value.blockingReasons.length) return { ...value, poolAvailable: false, availableRewardWei: null };
  return value;
}

/** One public stream and a bounded, single-flight fallback per deployment context. */
export function useRewardPoolLive(api: ApiClient, enabled: boolean, context: string,
  readContext: () => string, refreshRevision: number) {
  const [stored, setStored] = useState<{ context: string; data: PoolSnapshot | null; lastDisplayData: PoolSnapshot | null; loading: boolean; error: string | null }>(
    { context: "", data: null, lastDisplayData: null, loading: false, error: null });
  const generation = useRef(0);
  const refresh = useRef(() => {});
  const previousRefresh = useRef(refreshRevision);
  useEffect(() => {
    if (!enabled) return;
    const revision = ++generation.current;
    const abort = new AbortController();
    const current = () => !abort.signal.aborted && revision === generation.current && context === readContext();
    const publish = (data: PoolSnapshot | null, error: string | null, loading = false) => {
      if (current()) setStored(previous => !current() ? previous : ({ context, data, error, loading,
        lastDisplayData: data?.poolAvailable && data.dataStatus === "CURRENT" && data.availableRewardWei !== null
          ? data : previous.context === context ? previous.lastDisplayData : null }));
    };
    let stream: AbortController | null = null, pending = false, queued = false, live = false;
    let push = 0, lastRead = -Infinity, retryAt = 0, failures = 0, serverRetry = 1000, unsupported = false;
    const path = `/tokens/${ELYSIUM.token}/reward-pool`;
    const load = (force = false) => {
      if (!current()) return;
      if (pending) { if (force) queued = true; return; }
      if (!force && (live || performance.now() - lastRead < 15_000)) return;
      pending = true; lastRead = performance.now(); const atDispatch = push;
      api.get(path, rewardPoolSchema, abort.signal).then(value => {
        const data = validateElysiumPool(value);
        if (!live && atDispatch === push) publish(data, null);
      }).catch((error: unknown) => { if (!live && atDispatch === push) publish(null, errorMessage(error)); })
        .finally(() => { pending = false; if (queued && current()) { queued = false; load(true); } });
    };
    const connect = () => {
      if (!current() || stream || unsupported || performance.now() < retryAt || document.visibilityState === "hidden") return;
      const connection = new AbortController(); stream = connection;
      api.stream(`${path}/stream`, { signal: connection.signal, onRetry: value => { serverRetry = Math.max(1000, value); },
        onEvent: event => {
          if (!current() || connection.signal.aborted) return;
          if (event.event === "error") throw new Error("REWARD_POOL_STREAM_ERROR");
          if (event.event !== "reward-pool") return;
          const data = validateElysiumPool(rewardPoolSchema.parse(JSON.parse(event.data)));
          push++; live = true; failures = 0; publish(data, null);
        },
      }).then(() => { if (!connection.signal.aborted) throw new Error("REWARD_POOL_STREAM_CLOSED"); })
        .catch((error: unknown) => {
          if (!current() || connection.signal.aborted) return;
          live = false; push++; publish(null, errorMessage(error));
          failures = Math.min(6, failures + 1);
          retryAt = performance.now() + Math.max(serverRetry, Math.min(30000, 1000 * 2 ** (failures - 1)),
            error instanceof ApiError ? error.retryAfterMs ?? 0 : 0);
          unsupported = error instanceof ApiError && [401, 403, 404, 405, 410].includes(error.status);
          load();
        }).finally(() => { if (stream === connection) stream = null; });
    };
    const reload = () => { live = false; push++; publish(null, null, true); stream?.abort(); stream = null; load(true); connect(); };
    refresh.current = reload;
    publish(null, null, true); load(true); connect();
    const tick = () => { if (document.visibilityState !== "hidden") { load(); connect(); } };
    const visibility = () => {
      if (document.visibilityState === "hidden") { live = false; push++; stream?.abort(); stream = null; publish(null, "REWARD_POOL_PAUSED"); }
      else reload();
    };
    const timer = setInterval(tick, 1000);
    document.addEventListener("visibilitychange", visibility);
    return () => { abort.abort(); stream?.abort(); clearInterval(timer); document.removeEventListener("visibilitychange", visibility); refresh.current = () => {}; };
  }, [api, enabled, context, readContext]);
  useEffect(() => {
    if (previousRefresh.current !== refreshRevision) { previousRefresh.current = refreshRevision; refresh.current(); }
  }, [refreshRevision]);
  return enabled && stored.context === context ? stored : { context, data: null, lastDisplayData: null, loading: enabled, error: null };
}
